import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentRecordModal = ({ invoice, isOpen, onClose, onSave }) => {
  const [paymentData, setPaymentData] = useState({
    amount: invoice?.amount - (invoice?.paidAmount || 0) || 0,
    date: new Date().toISOString().split('T')[0],
    method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  if (!isOpen || !invoice) return null;

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const remainingAmount = invoice.amount - (invoice.paidAmount || 0);
  const isPartialPayment = paymentData.amount < remainingAmount;
  const isOverpayment = paymentData.amount > remainingAmount;

  const handleSave = () => {
    const payment = {
      ...paymentData,
      id: Date.now(),
      invoiceId: invoice.id,
      timestamp: new Date().toISOString()
    };
    onSave(payment);
  };

  const paymentMethods = [
    { value: 'bank_transfer', label: 'Transferencia Bancaria', icon: 'Building2' },
    { value: 'credit_card', label: 'Tarjeta de Crédito', icon: 'CreditCard' },
    { value: 'cash', label: 'Efectivo', icon: 'Banknote' },
    { value: 'check', label: 'Cheque', icon: 'FileText' },
    { value: 'paypal', label: 'PayPal', icon: 'Smartphone' },
    { value: 'other', label: 'Otro', icon: 'MoreHorizontal' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success-50 rounded-lg">
              <Icon name="CreditCard" size={20} className="text-success" />
            </div>
            <div>
              <h2 className="text-xl font-heading-semibold text-text-primary">
                Registrar Pago
              </h2>
              <p className="text-sm text-text-secondary">
                Factura {invoice.number}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-50 rounded-lg transition-micro"
          >
            <Icon name="X" size={20} className="text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Invoice Summary */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-text-secondary">Cliente</div>
                <div className="font-body-medium text-text-primary">{invoice.client}</div>
              </div>
              <div>
                <div className="text-sm text-text-secondary">Importe Total</div>
                <div className="font-body-medium text-text-primary">
                  {formatCurrency(invoice.amount)}
                </div>
              </div>
              <div>
                <div className="text-sm text-text-secondary">Ya Pagado</div>
                <div className="font-body-medium text-text-primary">
                  {formatCurrency(invoice.paidAmount || 0)}
                </div>
              </div>
              <div>
                <div className="text-sm text-text-secondary">Pendiente</div>
                <div className="font-body-medium text-error">
                  {formatCurrency(remainingAmount)}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Importe del Pago (€)
              </label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                min="0"
                step="0.01"
                max={remainingAmount * 1.1} // Allow slight overpayment
                className={isOverpayment ? 'border-warning' : ''}
              />
              
              {isPartialPayment && (
                <div className="mt-1 flex items-center space-x-1 text-warning">
                  <Icon name="AlertTriangle" size={14} />
                  <span className="text-xs">Pago parcial</span>
                </div>
              )}
              
              {isOverpayment && (
                <div className="mt-1 flex items-center space-x-1 text-error">
                  <Icon name="AlertCircle" size={14} />
                  <span className="text-xs">
                    Sobrepago de {formatCurrency(paymentData.amount - remainingAmount)}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Fecha del Pago
              </label>
              <Input
                type="date"
                value={paymentData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Método de Pago
              </label>
              <select
                value={paymentData.method}
                onChange={(e) => handleInputChange('method', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {paymentMethods.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Referencia/Número de Transacción
              </label>
              <Input
                type="text"
                value={paymentData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                placeholder="Número de transferencia, cheque, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Notas (Opcional)
              </label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Notas adicionales sobre el pago..."
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div className="space-y-2">
            <div className="text-sm font-body-medium text-text-primary">
              Importes Rápidos:
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleInputChange('amount', remainingAmount)}
              >
                Pago Completo ({formatCurrency(remainingAmount)})
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleInputChange('amount', remainingAmount / 2)}
              >
                50% ({formatCurrency(remainingAmount / 2)})
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleInputChange('amount', remainingAmount / 4)}
              >
                25% ({formatCurrency(remainingAmount / 4)})
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancelar
          </Button>
          
          <Button
            variant="success"
            onClick={handleSave}
            disabled={paymentData.amount <= 0}
            iconName="Check"
            iconSize={16}
          >
            Registrar Pago
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentRecordModal;